import React from 'react';
import styles from './CycleSelector.module.scss';
import { withInjectedRootStore, RootStore } from 'providers';
import { CyclesController } from '../../SubscriptionPage/Cycles/Cycles.controller';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { Box, TextButton } from '@wix/design-system';
import { CycleSelectorPopover } from './Popover/CycleSelectorPopover';

export type CycleSelectorProps = {
  rootStore: RootStore;
} & InjectedExperimentsProps;

const CycleSelector: React.FC<CycleSelectorProps> = observer(
  ({ rootStore, experiments }) => {
    const { appStore } = rootStore;
    const { t } = useTranslation();
    const controller = React.useMemo(
      () => new CyclesController({ rootStore, experiments }),
      [appStore.currentCycle, appStore.currentProduct, experiments],
    );
    const defaultProduct = controller.getCurrentOrDefaultProduct();
    const cycles = controller.getCycles(defaultProduct);

    // For Japanese package picker, show cycle names in months
    const isJapanesePackagePicker = appStore.isJapanesePackagePicker;
    const theme = appStore.isInSale ? 'sale' : 'regular';

    React.useEffect(() => {
      if (appStore.appContext.currentPlanDetails?.guid) {
        appStore.setCurrentCycle({
          cycle: appStore.appContext.currentPlanDetails.paymentCycle,
        });
      } else {
        appStore.setDefaultCycle(defaultProduct);
      }
      appStore.setCurrentProduct(defaultProduct);
    }, []);

    return (
      <Box className={styles[theme + 'Container']}>
        {cycles.map(({ cycleDetails, cellProps }) => {
          const isDisabled =
            controller.isCycleDisabledInPlansPage(cycleDetails);

          return (
            <CycleSelectorPopover
              cycleDetails={cycleDetails}
              isDisabled={isDisabled}
            >
              <TextButton
                key={cycleDetails.cycleId}
                className={[
                  styles.cycleButton,
                  appStore.currentCycle === cycleDetails.cycleId
                    ? styles.selected
                    : '',
                  isDisabled ? styles.disabled : '',
                  styles[theme],
                ].join(' ')}
                onClick={cellProps.onClick}
                disabled={isDisabled}
              >
                <Box display='block'>
                  <Box className={styles.label}>
                    {t(
                      CyclesController.getCycleNameKey(
                        cycleDetails.cycleId,
                        isJapanesePackagePicker,
                      ),
                    )}
                  </Box>
                  <Box
                    className={
                      appStore.currentCycle === cycleDetails.cycleId
                        ? styles.selectedSubLabel
                        : styles.subLabel
                    }
                  >
                    {controller.getCycleSavingsLabel(cycleDetails, t)}
                  </Box>
                </Box>
              </TextButton>
            </CycleSelectorPopover>
          );
        })}
      </Box>
    );
  },
);

export default withExperiments(withInjectedRootStore(CycleSelector));
